import { Dialog } from "@vellum/ui";
import type { HubFolderItem, LinkSchema } from "@vellum/vellum-api";
import { useState } from "react";

import { AddCard, EditableLinkCard, LinkCard } from "@/components/LinkCard";

interface FolderDialogProps {
  folder: HubFolderItem | null;
  isOpen: boolean;
  onClose: () => void;
  managing: boolean;
  onAddLink: (folder: HubFolderItem) => void;
  onEditLink: (link: LinkSchema) => void;
  onDeleteLink: (link: LinkSchema) => void;
  /** Persists the folder's links with `draggedId` moved to `targetIndex`. */
  onMoveLink: (draggedId: string, targetIndex: number) => void;
}

/**
 * A folder's links. Viewers get the usual link cards; in manage mode the
 * links can be edited, deleted, added and dragged into a new order.
 */
export function FolderDialog({
  folder,
  isOpen,
  onClose,
  managing,
  onAddLink,
  onEditLink,
  onDeleteLink,
  onMoveLink,
}: FolderDialogProps) {
  const [draggedId, setDraggedId] = useState<string | null>(null);

  if (!folder) {
    return null;
  }

  const dropOn = (targetIndex: number) => {
    if (draggedId) {
      onMoveLink(draggedId, targetIndex);
    }
    setDraggedId(null);
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={folder.title}
      icon={`iconoir-${folder.icon ?? "folder"}`}
      iconColor="green"
    >
      {folder.description ? (
        <p className="mt-1 text-sm text-gray-600">{folder.description}</p>
      ) : null}
      {folder.links.length === 0 && !managing ? (
        <p className="mt-4 text-sm text-gray-500">This folder is empty.</p>
      ) : (
        <div
          className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2"
          onDragOver={(event) =>
            managing ? event.preventDefault() : undefined
          }
          onDrop={() => (managing ? dropOn(folder.links.length) : undefined)}
        >
          {folder.links.map((link, index) =>
            managing ? (
              <EditableLinkCard
                key={link.id}
                link={link}
                dragging={draggedId === link.id}
                onEdit={() => onEditLink(link)}
                onDelete={() => onDeleteLink(link)}
                onDragStart={() => setDraggedId(link.id)}
                onDragEnd={() => setDraggedId(null)}
                onDropOn={() =>
                  draggedId === link.id ? setDraggedId(null) : dropOn(index)
                }
              />
            ) : (
              <LinkCard key={link.id} link={link} />
            ),
          )}
          {managing ? (
            <AddCard
              label="Add link to this folder"
              icon="plus"
              onClick={() => onAddLink(folder)}
            />
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
